import axios, { AxiosError, AxiosResponse } from 'axios'
import { config } from '@/config'
import { formatResponse } from './helpers/formatResponse'
import type {
  FilterValuesResponse,
  FilterValueDiscoveryContext,
} from '@/types/DashboardFilterDiscovery.types'

/**
 * DashboardFiltersAPI — credential-driven filter-value discovery.
 *
 * Backed by `GET /v1/dashboard-domains/:domainId/filters/:filterCode/values`
 * (Stage 2A of the Dashboard Filter Discovery feature, TDD §Backend Side).
 *
 * The success body is the **raw** response shape `{ values: [...] }` —
 * NOT wrapped in the legacy `{ success, data, messages }` `TCSAPIResponse`
 * envelope. Per CEO 2026-04-28 directive, TCS is moving away from the
 * envelope/wrapper for new endpoints. We therefore bypass `formatResponse`
 * (which unwraps the envelope) on the success path and consume `resp.data`
 * directly. The error path still uses `formatResponse` because Nest error
 * bodies retain the `{ message }` / `{ messages }` shape.
 */
export const DashboardFiltersAPI = {
  getFilterValues: async (
    token: string,
    args: {
      /** Fully qualified domainId, e.g. `campminder.enrollment`. */
      domainId: string
      /** Filter code on the domain, e.g. `season`. */
      filterCode: string
      /** UUID of the credential driving discovery (selected at Step 2). */
      credentialId: string
      /**
       * Parent-filter context (e.g. for `enrollment.session`, pass the
       * selected seasons under the `season` key). Empty / undefined when no
       * parent has been selected.
       */
      context?: FilterValueDiscoveryContext
    }
  ): Promise<{
    data: FilterValuesResponse | undefined
    err: string | undefined
    status: number | undefined
    errorCode: string | undefined
    /**
     * Retry-after window in seconds when the backend returns
     * `error: 'RATE_LIMITED'` (503). Undefined on any other response. The
     * FE renders a countdown affordance and auto-retries when this elapses.
     * Optional (rather than `number | undefined`) so existing mocks in
     * spec files that predate this field continue to type-check without
     * a per-test backfill. Bug #3 (2026-05-14).
     */
    retryAfterSeconds?: number
  }> => {
    try {
      const params: Record<string, string> = { credentialId: args.credentialId }
      if (args.context && Object.keys(args.context).length > 0) {
        params.context = JSON.stringify(args.context)
      }
      const resp: AxiosResponse<FilterValuesResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/dashboard-domains/${encodeURIComponent(args.domainId)}/filters/${encodeURIComponent(args.filterCode)}/values`,
        headers: { Authorization: `Bearer ${token}` },
        params,
      })
      // Raw shape — no envelope unwrap. resp.data IS the FilterValuesResponse.
      return {
        data: resp.data,
        err: undefined,
        status: resp.status,
        errorCode: undefined,
        retryAfterSeconds: undefined,
      }
    } catch (error) {
      const axiosErr = error as AxiosError<{
        error?: string
        reason?: string
        retryAfterSeconds?: number
      }>
      const status = axiosErr.response?.status
      // Backend signals credential expiration via either `error: 'CREDENTIAL_EXPIRED'`
      // or `reason: 'credential_expired'` on a 503. Surface both possibilities for
      // the service-layer thunk to discriminate without re-parsing the body.
      // RATE_LIMITED carries `retryAfterSeconds` (Bug #3) — preserve it.
      const body = axiosErr.response?.data
      const errorCode = body?.error ?? body?.reason
      const retryAfterSeconds =
        typeof body?.retryAfterSeconds === 'number'
          ? body.retryAfterSeconds
          : undefined
      const formatted = formatResponse<FilterValuesResponse>(null, axiosErr)
      return {
        data: formatted.data,
        err: formatted.err,
        status,
        errorCode,
        retryAfterSeconds,
      }
    }
  },
}
